import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Stack,
  // InputAdornment,
  Tabs,
  Tab,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Checkbox,
  FormControlLabel,
  // Divider,
  Avatar,
  useMediaQuery,
} from '@mui/material';

import {
  Add as AddIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { DataContext } from '../../context/DataContext';

const AdvancePaymentManagement = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    customers,
    addAdvancePayment,
    withdrawAdvance,
    getCustomerAdvanceHistory
  } = useContext(DataContext);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [advanceHistory, setAdvanceHistory] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [modeWiseBalances, setModeWiseBalances] = useState({
    cash: 0,
    online: 0,
    oldGold: 0,
    oldSilver: 0
  });

  const [paymentModes, setPaymentModes] = useState([
    { mode: 'cash', amount: '', enabled: false, label: 'Cash' },
    { mode: 'online', amount: '', enabled: false, label: 'Online' },
    { mode: 'oldGold', amount: '', enabled: false, label: 'Old Gold' },
    { mode: 'oldSilver', amount: '', enabled: false, label: 'Old Silver' },
  ]);

  const [transactionDate, setTransactionDate] = useState(new Date());
  const [remarks, setRemarks] = useState('');

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMode, setWithdrawMode] = useState('cash');
  const [withdrawReason, setWithdrawReason] = useState('');

  useEffect(() => {
    if (customerSearch.trim()) {
      const filtered = customers.filter(c =>
        c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.contact?.toString().includes(customerSearch)
      );

      setCustomerSuggestions(filtered.slice(0, 10));
    } else {
      setCustomerSuggestions([]);
    }
  }, [customerSearch, customers]);

  const calculateModeWiseBalances = (history) => {
    const balances = {
      cash: 0,
      online: 0,
      oldGold: 0,
      oldSilver: 0
    };

    history.forEach(record => {
      if (record.type === 'add' && record.paymentBreakdown) {
        Object.entries(record.paymentBreakdown).forEach(([mode, amount]) => {
          if (balances[mode] !== undefined) {
            balances[mode] += amount;
          }
        });
      } else if (record.type === 'used' && record.paymentBreakdown) {
        Object.entries(record.paymentBreakdown).forEach(([mode, amount]) => {
          if (balances[mode] !== undefined) {
            balances[mode] -= amount;
          }
        });
      }
    });

    return balances;
  };

  const loadAdvanceHistory = useCallback(async () => {
    if (!selectedCustomer) return;

    const history = await getCustomerAdvanceHistory(selectedCustomer.id);

    setAdvanceHistory(history);

    let totalGiven = 0;
    let totalUsed = 0;

    history.forEach(record => {
      if (record.type === 'add') {
        totalGiven += record.amount;
      } else if (
        record.type === 'used' ||
        record.type === 'withdraw'
      ) {
        totalUsed += record.amount;
      }
    });

    const balance = totalGiven - totalUsed;

    setCurrentBalance(balance);

    const modeBalances = calculateModeWiseBalances(history);
    setModeWiseBalances(modeBalances);

  }, [getCustomerAdvanceHistory, selectedCustomer]);

  useEffect(() => {
    if (selectedCustomer) {
      loadAdvanceHistory();
    }
  }, [selectedCustomer, loadAdvanceHistory]);

  const getTotalAdvanceAmount = () => {
    return paymentModes
      .filter(mode => mode.enabled && mode.amount)
      .reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
  };

  const getPaymentModesObject = () => {
    const modes = {};

    paymentModes.forEach(mode => {
      if (
        mode.enabled &&
        mode.amount &&
        parseFloat(mode.amount) > 0
      ) {
        modes[mode.mode] = parseFloat(mode.amount);
      }
    });

    return modes;
  };

  const togglePaymentMode = (index) => {
    const updatedModes = [...paymentModes];

    updatedModes[index].enabled = !updatedModes[index].enabled;

    if (!updatedModes[index].enabled) {
      updatedModes[index].amount = '';
    }

    setPaymentModes(updatedModes);
  };

  const updatePaymentAmount = (index, amount) => {
    const updatedModes = [...paymentModes];
    updatedModes[index].amount = amount;
    setPaymentModes(updatedModes);
  };

  const resetPaymentModes = () => {
    setPaymentModes([
      { mode: 'cash', amount: '', enabled: false, label: 'Cash' },
      { mode: 'online', amount: '', enabled: false, label: 'Online' },
      { mode: 'oldGold', amount: '', enabled: false, label: 'Old Gold' },
      { mode: 'oldSilver', amount: '', enabled: false, label: 'Old Silver' },
    ]);
  };

  const handleAddAdvance = async () => {
    if (!selectedCustomer) {
      alert('Please select customer');
      return;
    }

    const enabledModes = paymentModes.filter(
      mode =>
        mode.enabled &&
        mode.amount &&
        parseFloat(mode.amount) > 0
    );

    if (enabledModes.length === 0) {
      alert('Please select payment mode');
      return;
    }

    const totalAmount = getTotalAdvanceAmount();
    const paymentModesObj = getPaymentModesObject();

    try {
      const result = await addAdvancePayment({
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        paymentModes: paymentModesObj,
        totalAmount,
        date: transactionDate,
        remarks,
        recordedBy: "System"
      });

      if (result.success) {
        setSnackbar({
          open: true,
          message: `₹${totalAmount} added successfully`,
          severity: 'success'
        });

        resetPaymentModes();
        setRemarks('');
        setAddDialogOpen(false);

        await loadAdvanceHistory();
      }

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed',
        severity: 'error'
      });
    }
  };

  const handleWithdrawAdvance = async () => {
    if (!selectedCustomer) {
      alert('Please select customer');
      return;
    }

    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert('Enter valid amount');
      return;
    }

    if (parseFloat(withdrawAmount) > currentBalance) {
      alert('Insufficient balance');
      return;
    }

    try {
      const result = await withdrawAdvance({
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        amount: parseFloat(withdrawAmount),
        paymentMode: withdrawMode,
        reason: withdrawReason,
        date: transactionDate,
      });

      if (result.success) {
        setSnackbar({
          open: true,
          message: `₹${withdrawAmount} withdrawn`,
          severity: 'success'
        });

        setWithdrawAmount('');
        setWithdrawReason('');
        setWithdrawMode('cash');
        setWithdrawDialogOpen(false);

        await loadAdvanceHistory();
      }

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed',
        severity: 'error'
      });
    }
  };

  const getTotalAdvanceGiven = () => {
    return advanceHistory
      .filter(record => record.type === 'add')
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const getTotalAdvanceUsed = () => {
    return advanceHistory
      .filter(
        record =>
          record.type === 'used' ||
          record.type === 'withdraw'
      )
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const cardStyle = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    boxShadow: isDark
      ? "0 10px 30px rgba(0,0,0,0.35)"
      : "0 10px 30px rgba(15,23,42,0.08)",
    backdropFilter: "blur(10px)",
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: '100vh',
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: '1500px', mx: 'auto' }}>

          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight={800}
                sx={{
                  background: isDark
                    ? "linear-gradient(135deg,#93C5FD,#60A5FA)"
                    : "linear-gradient(135deg,#0F172A,#334155)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Advance Payment Management
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                mt={1}
              >
                Manage customer advance balances & transactions
              </Typography>
            </Box>

            {selectedCustomer && (
              <Paper
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <Typography variant="caption">
                  Current Balance
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                >
                  ₹{currentBalance.toFixed(2)}
                </Typography>
              </Paper>
            )}
          </Box>

          <Grid container spacing={3}>

            {/* SIDEBAR */}
            <Grid item xs={12} lg={3}>

              <Card sx={{ p: 3, ...cardStyle }}>

                <Typography
                  variant="h6"
                  fontWeight={700}
                  mb={2}
                >
                  Select Customer
                </Typography>

                <TextField
                  fullWidth
                  label="Search Customer"
                  value={customerSearch}
                  onChange={(e) =>
                    setCustomerSearch(e.target.value)
                  }
                  placeholder="Name or contact..."
                />

                {customerSuggestions.length > 0 && (
                  <Paper
                    sx={{
                      mt: 2,
                      maxHeight: 350,
                      overflow: 'auto',
                      borderRadius: 2,
                    }}
                  >
                    {customerSuggestions.map((customer) => (
                      <Stack
                        key={customer.id}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          transition: '0.2s',
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          }
                        }}
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setCustomerSearch(customer.name);
                          setCustomerSuggestions([]);
                        }}
                      >
                        <Avatar>
                          {customer.name?.charAt(0)}
                        </Avatar>

                        <Box>
                          <Typography fontWeight={700}>
                            {customer.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {customer.contact}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Paper>
                )}

                {selectedCustomer && (
                  <Alert
                    severity="success"
                    sx={{
                      mt: 3,
                      borderRadius: 2,
                    }}
                  >
                    <Typography fontWeight={700}>
                      {selectedCustomer.name}
                    </Typography>

                    <Typography variant="caption">
                      Balance:
                      ₹{selectedCustomer?.TotalAdvanceBalance || 0}
                    </Typography>
                  </Alert>
                )}

              </Card>

            </Grid>

            {/* MAIN CONTENT */}
            <Grid item xs={12} lg={9}>

              {selectedCustomer ? (
                <Stack spacing={3}>

                  {/* ACTION CARD */}
                  <Card sx={{ p: 3, ...cardStyle }}>

                    <Stack
                      direction={{
                        xs: "column",
                        md: "row"
                      }}
                      justifyContent="space-between"
                      alignItems={{
                        xs: "stretch",
                        md: "center"
                      }}
                      spacing={3}
                    >

                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Available Balance
                        </Typography>

                        <Typography
                          variant={isMobile ? "h4" : "h3"}
                          fontWeight={800}
                        >
                          ₹{currentBalance.toFixed(2)}
                        </Typography>
                      </Box>

                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row"
                        }}
                        spacing={2}
                        width={{
                          xs: "100%",
                          sm: "auto"
                        }}
                      >
                        <Button
                          fullWidth={isMobile}
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            resetPaymentModes();
                            setAddDialogOpen(true);
                          }}
                          sx={{
                            borderRadius: 3,
                            height: 50,
                          }}
                        >
                          Add Advance
                        </Button>

                        <Button
                          fullWidth={isMobile}
                          variant="outlined"
                          startIcon={<TrendingDownIcon />}
                          onClick={() =>
                            setWithdrawDialogOpen(true)
                          }
                          disabled={currentBalance <= 0}
                          sx={{
                            borderRadius: 3,
                            height: 50,
                          }}
                        >
                          Withdraw
                        </Button>
                      </Stack>

                    </Stack>

                  </Card>

                  {/* SUMMARY */}
                  <Grid container spacing={3}>

                    <Grid item xs={12} md={4}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                        }}
                      >
                        <Stack direction="row" spacing={2}>
                          <TrendingUpIcon color="success" />

                          <Box>
                            <Typography variant="caption">
                              Total Added
                            </Typography>

                            <Typography
                              variant="h5"
                              fontWeight={800}
                            >
                              ₹{getTotalAdvanceGiven().toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                        }}
                      >
                        <Stack direction="row" spacing={2}>
                          <TrendingDownIcon color="error" />

                          <Box>
                            <Typography variant="caption">
                              Total Used
                            </Typography>

                            <Typography
                              variant="h5"
                              fontWeight={800}
                            >
                              ₹{getTotalAdvanceUsed().toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }}
                      >
                        <Stack direction="row" spacing={2}>
                          <WalletIcon color="primary" />

                          <Box>
                            <Typography variant="caption">
                              Current Balance
                            </Typography>

                            <Typography
                              variant="h5"
                              fontWeight={800}
                            >
                              ₹{currentBalance.toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                  </Grid>

                  {/* TABS */}
                  <Card sx={{ ...cardStyle }}>

                    <Tabs
                      value={selectedTab}
                      onChange={(e, v) => setSelectedTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ px: 2, pt: 2 }}
                    >
                      <Tab
                        icon={<HistoryIcon />}
                        label="Transactions"
                      />

                      <Tab
                        icon={<WalletIcon />}
                        label="Summary"
                      />
                    </Tabs>

                    <Box sx={{ p: 3 }}>

                      {/* TRANSACTION TAB */}
                      {selectedTab === 0 && (

                        <>
                          {/* MOBILE CARDS */}
                          {isMobile ? (

                            <Stack spacing={2}>

                              {advanceHistory.map((record, index) => (

                                <Paper
                                  key={index}
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                  }}
                                >
                                  <Stack spacing={1.5}>

                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                    >
                                      <Typography
                                        fontWeight={700}
                                      >
                                        {record.date}
                                      </Typography>

                                      <Chip
                                        size="small"
                                        label={
                                          record.type === 'add'
                                            ? 'Added'
                                            : 'Withdraw'
                                        }
                                        color={
                                          record.type === 'add'
                                            ? 'success'
                                            : 'error'
                                        }
                                      />
                                    </Stack>

                                    <Typography
                                      variant="h6"
                                      fontWeight={800}
                                      color={
                                        record.type === 'add'
                                          ? 'success.main'
                                          : 'error.main'
                                      }
                                    >
                                      {record.type === 'add'
                                        ? '+'
                                        : '-'}
                                      ₹{record.amount.toFixed(2)}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                    >
                                      {record.remarks ||
                                        record.reason ||
                                        '-'}
                                    </Typography>

                                  </Stack>
                                </Paper>

                              ))}

                            </Stack>

                          ) : (

                            /* DESKTOP TABLE */

                            <TableContainer
                              component={Paper}
                              sx={{
                                borderRadius: 2,
                              }}
                            >
                              <Table>

                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Remarks</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>

                                  {advanceHistory.map((record, index) => (

                                    <TableRow key={index}>

                                      <TableCell>
                                        {record.date}
                                      </TableCell>

                                      <TableCell>
                                        <Chip
                                          size="small"
                                          label={
                                            record.type === 'add'
                                              ? 'Added'
                                              : 'Withdraw'
                                          }
                                          color={
                                            record.type === 'add'
                                              ? 'success'
                                              : 'error'
                                          }
                                        />
                                      </TableCell>

                                      <TableCell>
                                        <Typography
                                          fontWeight={700}
                                          color={
                                            record.type === 'add'
                                              ? 'success.main'
                                              : 'error.main'
                                          }
                                        >
                                          {record.type === 'add'
                                            ? '+'
                                            : '-'}
                                          ₹{record.amount.toFixed(2)}
                                        </Typography>
                                      </TableCell>

                                      <TableCell>
                                        {record.remarks ||
                                          record.reason ||
                                          '-'}
                                      </TableCell>

                                    </TableRow>

                                  ))}

                                </TableBody>

                              </Table>
                            </TableContainer>

                          )}

                        </>
                      )}

                      {/* SUMMARY TAB */}
                      {selectedTab === 1 && (

                        <Grid container spacing={3}>

                          {Object.entries(modeWiseBalances).map(
                            ([mode, amount]) => (

                              amount > 0 && (

                                <Grid
                                  item
                                  xs={6}
                                  md={3}
                                  key={mode}
                                >
                                  <Paper
                                    sx={{
                                      p: 3,
                                      borderRadius: 2,
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {mode}
                                    </Typography>

                                    <Typography
                                      variant="h6"
                                      fontWeight={800}
                                      mt={1}
                                    >
                                      ₹{amount.toFixed(2)}
                                    </Typography>
                                  </Paper>
                                </Grid>

                              )
                            )
                          )}

                        </Grid>

                      )}

                    </Box>

                  </Card>

                </Stack>

              ) : (

                <Card
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    ...cardStyle
                  }}
                >
                  <WalletIcon
                    sx={{
                      fontSize: 70,
                      color: 'text.secondary',
                      mb: 2,
                    }}
                  />

                  <Typography
                    variant="h6"
                    color="text.secondary"
                  >
                    Select customer to manage advance payments
                  </Typography>
                </Card>

              )}

            </Grid>

          </Grid>

          {/* ADD DIALOG */}
          <Dialog
            open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Add Advance Payment
            </DialogTitle>

            <DialogContent>

              <Stack spacing={3} mt={2}>

                {paymentModes.map((payment, index) => (

                  <Stack
                    key={payment.mode}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={payment.enabled}
                          onChange={() =>
                            togglePaymentMode(index)
                          }
                        />
                      }
                      label={payment.label}
                      sx={{ minWidth: 120 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="Amount"
                      value={payment.amount}
                      disabled={!payment.enabled}
                      onChange={(e) =>
                        updatePaymentAmount(
                          index,
                          e.target.value
                        )
                      }
                    />

                  </Stack>

                ))}

                <Alert severity="info">
                  Total:
                  ₹{getTotalAdvanceAmount().toFixed(2)}
                </Alert>

                <DatePicker
                  label="Transaction Date"
                  value={transactionDate}
                  onChange={(date) =>
                    setTransactionDate(date)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />

                <TextField
                  label="Remarks"
                  multiline
                  rows={3}
                  value={remarks}
                  onChange={(e) =>
                    setRemarks(e.target.value)
                  }
                />

              </Stack>

            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() =>
                  setAddDialogOpen(false)
                }
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleAddAdvance}
              >
                Add Advance
              </Button>
            </DialogActions>
          </Dialog>

          {/* WITHDRAW DIALOG */}
          <Dialog
            open={withdrawDialogOpen}
            onClose={() =>
              setWithdrawDialogOpen(false)
            }
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Withdraw Advance
            </DialogTitle>

            <DialogContent>

              <Stack spacing={3} mt={2}>

                <Alert severity="info">
                  Available:
                  ₹{currentBalance.toFixed(2)}
                </Alert>

                <FormControl fullWidth>

                  <InputLabel>
                    Withdrawal Mode
                  </InputLabel>

                  <Select
                    value={withdrawMode}
                    label="Withdrawal Mode"
                    onChange={(e) =>
                      setWithdrawMode(e.target.value)
                    }
                  >
                    <MenuItem value="cash">
                      Cash
                    </MenuItem>

                    <MenuItem value="online">
                      Online
                    </MenuItem>

                    <MenuItem value="oldGold">
                      Old Gold
                    </MenuItem>

                    <MenuItem value="oldSilver">
                      Old Silver
                    </MenuItem>

                  </Select>

                </FormControl>

                <TextField
                  label="Amount"
                  value={withdrawAmount}
                  onChange={(e) =>
                    setWithdrawAmount(e.target.value)
                  }
                />

                <TextField
                  label="Reason"
                  multiline
                  rows={3}
                  value={withdrawReason}
                  onChange={(e) =>
                    setWithdrawReason(e.target.value)
                  }
                />

              </Stack>

            </DialogContent>

            <DialogActions sx={{ p: 2 }}>

              <Button
                onClick={() =>
                  setWithdrawDialogOpen(false)
                }
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleWithdrawAdvance}
              >
                Withdraw
              </Button>

            </DialogActions>

          </Dialog>

          {/* SNACKBAR */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={() =>
              setSnackbar({
                ...snackbar,
                open: false
              })
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
          >
            <Alert severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>

        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancePaymentManagement;